import { Home, CircleCheck, Server, EyeOff, Settings2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import {
  LegalPage,
  LegalSection,
  LegalHeading,
  LegalList,
  LegalTable,
} from "@/components/blocks/LegalPage";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "stand", label: "Aktueller Stand", icon: CircleCheck },
  { id: "geplant", label: "Mit der Plattform", icon: Server },
  { id: "analyse", label: "Keine Analyse", icon: EyeOff },
  { id: "verwalten", label: "Selbst verwalten", icon: Settings2 },
  { id: "aenderungen", label: "Änderungen", icon: RefreshCw },
];

const linkClass =
  "text-white/80 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300";

const GEPLANT = [
  [
    "Sitzung",
    "Hält euch nach dem Login angemeldet. Ohne dieses Cookie müsstet ihr euch bei jedem Seitenwechsel neu anmelden.",
    "Bis zum Abmelden, längstens einige Tage",
    "Notwendig",
  ],
  [
    "Sicherheitstoken",
    "Schützt Formulare davor, dass fremde Seiten in eurem Namen Aktionen auslösen.",
    "Dauer der Sitzung",
    "Notwendig",
  ],
  [
    "Einstellungen",
    "Merkt sich Entscheidungen, die ihr selbst getroffen habt, etwa den gewählten Zeitraum im Dashboard.",
    "Bis zu 12 Monate",
    "Notwendig",
  ],
  [
    "Zahlungsdienstleister",
    "Wird beim Bezahlvorgang gesetzt und dient der Betrugserkennung.",
    "Nach Vorgabe des Anbieters",
    "Notwendig",
  ],
];

export default function Cookies() {
  return (
    <LegalPage
      eyebrow="Rechtliches"
      title="Cookies"
      lead="Was MatchGate auf eurem Gerät speichert - und was nicht. Kurz zusammengefasst: derzeit nichts."
      sidebarItems={SIDEBAR_ITEMS}
    >
      <LegalSection id="stand" title="Aktueller Stand: keine Cookies">
        <p>
          Diese Website setzt{" "}
          <strong className="text-white/85">keine Cookies</strong> und speichert
          auch sonst nichts auf eurem Gerät. Weder der lokale Browser-Speicher
          noch der Sitzungsspeicher werden verwendet.
        </p>
        <p>Ebenfalls nicht eingebunden sind:</p>
        <LegalList
          items={[
            "Analyse- oder Statistikdienste",
            "Werbenetzwerke und Retargeting",
            "Schaltflächen oder Plugins sozialer Netzwerke",
            "Chatbots oder eingebettete Videos",
          ]}
        />
        <p>
          Weil nichts gespeichert und nichts ausgelesen wird, ist auch keine
          Einwilligungsabfrage nötig. Ihr seht auf dieser Website deshalb kein
          Cookie-Banner. Was beim Aufruf technisch trotzdem anfällt - etwa die
          IP-Adresse in den Server-Protokollen -, steht in der{" "}
          <Link to="/datenschutz" className={linkClass}>
            Datenschutzerklärung
          </Link>
          .
        </p>

        <LegalHeading>Was ein Cookie überhaupt ist</LegalHeading>
        <p>
          Ein Cookie ist eine kleine Textdatei, die eine Website in eurem
          Browser ablegt und beim nächsten Aufruf wieder auslesen kann. Manche
          sind für den Betrieb unverzichtbar - ohne sie funktioniert etwa kein
          Login. Andere dienen dazu, Verhalten über längere Zeit
          nachzuvollziehen. Nur für die zweite Sorte braucht es eine
          Einwilligung.
        </p>
      </LegalSection>

      <LegalSection id="geplant" title="Was mit der Plattform dazukommt">
        <p>
          Sobald der angemeldete Bereich von MatchGate startet, lassen sich
          einige Cookies nicht vermeiden. Alle folgenden sind technisch
          notwendig im Sinne des § 25 Absatz 2 Nummer 2 TDDDG - sie dürfen ohne
          Einwilligung gesetzt werden, weil der Dienst ohne sie nicht
          funktioniert:
        </p>
        <LegalTable
          head={["Cookie", "Zweck", "Speicherdauer", "Einordnung"]}
          rows={GEPLANT}
        />
        <p className="text-white/35 text-sm">
          Diese Übersicht beschreibt den geplanten Stand und wird konkretisiert,
          sobald die Plattform tatsächlich läuft. Namen, Speicherdauern und der
          Zahlungsdienstleister stehen dann fest.
        </p>
      </LegalSection>

      <LegalSection id="analyse" title="Keine Analyse-Cookies">
        <p>
          Wir haben uns bewusst gegen Webanalyse entschieden. Es gibt kein
          Google Analytics, keine Reichweitenmessung und keine Werkzeuge, die
          euer Verhalten über mehrere Besuche hinweg zusammenführen.
        </p>
        <p>
          Sollte sich das ändern, aktualisieren wir diese Seite vorher - und
          falls das eingesetzte Werkzeug eine Einwilligung erfordert, fragen wir
          euch, bevor irgendetwas gesetzt wird.
        </p>
      </LegalSection>

      <LegalSection id="verwalten" title="Cookies selbst verwalten">
        <p>
          Unabhängig von dieser Website könnt ihr Cookies jederzeit in eurem
          Browser einsehen, löschen oder von vornherein blockieren. Die
          Einstellung findet sich üblicherweise unter „Datenschutz und
          Sicherheit":
        </p>
        <LegalList
          items={[
            "Chrome: Einstellungen, Datenschutz und Sicherheit, Drittanbieter-Cookies",
            "Firefox: Einstellungen, Datenschutz & Sicherheit, Cookies und Website-Daten",
            "Safari: Einstellungen, Datenschutz, Website-Daten verwalten",
            "Edge: Einstellungen, Cookies und Websiteberechtigungen",
          ]}
        />
        <p>
          Wenn ihr technisch notwendige Cookies vollständig blockiert, wird der
          angemeldete Bereich von MatchGate später nicht funktionieren. Für den
          öffentlichen Teil dieser Website spielt es keine Rolle - hier gibt es
          ohnehin nichts zu blockieren.
        </p>
      </LegalSection>

      <LegalSection id="aenderungen" title="Änderungen dieser Übersicht">
        <p>
          Wir passen diese Seite an, sobald sich der Einsatz von Cookies ändert.
          Der Stand ist oben vermerkt. Es gilt jeweils die hier veröffentlichte
          Fassung.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
